import { useState } from 'react';
import { toast } from 'sonner';
import { Truck, X } from 'lucide-react';
import api from '../../utils/api';

/**
 * Per-retailer delivery_enabled toggle for the admin retailers list.
 * Hits PUT /api/admin/retailers/{drlpId}/delivery-enabled with {enabled: bool}.
 *
 * Uses the shared `api` axios client so the Bearer token (localStorage.token)
 * and REACT_APP_BACKEND_URL are picked up automatically.
 */
export default function RetailerDeliveryToggle({ drlpId, initialEnabled = false, onChange }) {
  const [enabled, setEnabled] = useState(!!initialEnabled);
  const [loading, setLoading] = useState(false);

  const toggle = async (e) => {
    e?.stopPropagation?.();
    if (loading) return;
    setLoading(true);
    const next = !enabled;
    try {
      await api.put(`/admin/retailers/${drlpId}/delivery-enabled`, { enabled: next });
      setEnabled(next);
      onChange?.(next);
      toast.success(next ? 'Delivery enabled' : 'Delivery disabled (pickup-only)');
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Unknown error';
      toast.error(`Could not update: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={loading}
      data-testid={`delivery-toggle-${drlpId}`}
      title={enabled ? 'Delivery enabled · click to switch to pickup-only' : 'Pickup-only · click to enable delivery'}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition ${
        enabled
          ? 'bg-orange-100 text-orange-700 hover:bg-orange-200'
          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
      } ${loading ? 'opacity-60 cursor-wait' : 'cursor-pointer'}`}
    >
      {enabled ? <Truck className="w-3 h-3" /> : <X className="w-3 h-3" />}
      {enabled ? 'Delivery ON' : 'Pickup only'}
    </button>
  );
}
